import { z } from "zod";

const BD_PHONE_REGEX = /^(\+?880|0)1[3-9]\d{8}$/;
const STRIP_CHARS = /[\s\-()]/g;

/**
 * Normalize any recognizable Bangladeshi mobile number to canonical +880 format.
 * Returns `null` if the input is not a valid BD number.
 *
 * Accepted inputs: `01712345678`, `+8801712345678`, `8801712345678`
 * Output: `+8801712345678`
 */
export function normalizeBdPhone(raw: string): string | null {
  const stripped = raw.replace(STRIP_CHARS, "");

  if (!BD_PHONE_REGEX.test(stripped)) {
    return null;
  }

  if (stripped.startsWith("+880")) {
    return stripped;
  }

  if (stripped.startsWith("880")) {
    return `+${stripped}`;
  }

  if (stripped.startsWith("0")) {
    return `+88${stripped}`;
  }

  return null;
}

const CANONICAL_PREFIX = /^\+880/;
const PLUS_PREFIX = /^\+/;

/**
 * Canonical storage form for a Bangladeshi mobile number: local `01712345678`.
 * Any recognizable format (with/without country code, spaces, dashes, parens)
 * collapses to this single string so storage and lookup never drift.
 * Returns `null` if the input is not a valid BD number.
 */
export function canonicalBdPhone(raw: string): string | null {
  const e164 = normalizeBdPhone(raw);

  if (!e164) {
    return null;
  }

  return e164.replace(CANONICAL_PREFIX, "0");
}

/**
 * All equivalent stored forms of a BD number, for dual-lookup of legacy rows
 * that were persisted before canonicalization existed.
 * e.g. `["01712345678", "+8801712345678", "8801712345678"]`
 * Returns an empty array if the input is not a valid BD number.
 */
export function bdPhoneCandidates(raw: string): string[] {
  const e164 = normalizeBdPhone(raw);

  if (!e164) {
    return [];
  }

  return [
    e164.replace(CANONICAL_PREFIX, "0"),
    e164,
    e164.replace(PLUS_PREFIX, ""),
  ];
}

/**
 * Format a canonical +880 number for display: `01712-345678`
 */
export function formatBdPhoneDisplay(canonical: string): string {
  const local = canonical.replace(CANONICAL_PREFIX, "0");

  return `${local.slice(0, 5)}-${local.slice(5)}`;
}

const BD_PHONE_ERROR =
  "Please enter a valid Bangladeshi mobile number (e.g., 01712345678)";

/**
 *
 */
export const bdPhoneSchema = z
  .string()
  .min(1, BD_PHONE_ERROR)
  .refine((val) => normalizeBdPhone(val) !== null, { message: BD_PHONE_ERROR });

/**
 *
 */
export const optionalBdPhoneSchema = z
  .string()
  .nullable()
  .optional()
  .refine(
    (val) => {
      if (val === undefined || val === null || val.trim() === "") {
        return true;
      }

      return normalizeBdPhone(val) !== null;
    },
    { message: BD_PHONE_ERROR }
  );
